import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, ShoppingCart, User, Menu, X, Phone, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Logo, COLLECTIONS_NAV } from "@/components/Brand";
import { POPULAR_GUIDES, getGuide } from "@/data/guides";
import { api, gbp } from "@/lib/api";
import CartDrawer from "@/components/CartDrawer";

const POPULAR = POPULAR_GUIDES.map((s) => getGuide(s)).filter(Boolean);

export default function Header() {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [mobile, setMobile] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [sugOpen, setSugOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => { setMobile(false); setSugOpen(false); }, [loc.pathname, loc.search]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(() => {
      api.get("/products", { params: { search: term } })
        .then((r) => { setSuggestions(r.data.slice(0, 6)); setSugOpen(true); })
        .catch(() => setSuggestions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const navLinks = useMemo(() => COLLECTIONS_NAV.filter((c) => c.slug !== "bleed-vents"), []);

  const submitSearch = (e) => {
    e.preventDefault();
    setSugOpen(false);
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const pickSuggestion = (slug) => {
    setSugOpen(false);
    setQ("");
    nav(`/products/${slug}`);
  };

  const renderSuggestions = () => (
    sugOpen && suggestions.length > 0 ? (
      <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-border rounded-sm shadow-hard py-1.5 z-50 max-h-[70vh] overflow-auto" data-testid="search-suggestions">
        {suggestions.map((p) => (
          <button key={p.slug} type="button" onMouseDown={(e) => { e.preventDefault(); pickSuggestion(p.slug); }}
            data-testid={`search-suggestion-${p.slug}`}
            className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted transition-colors">
            <img src={p.image} alt="" className="w-9 h-9 rounded-sm border border-border object-contain bg-white shrink-0" />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium truncate">{p.name}</span>
              <span className="block text-xs text-muted-foreground">{p.brand}</span>
            </span>
            <span className="text-sm font-semibold text-primary shrink-0">{gbp(p.price)}</span>
          </button>
        ))}
        <button type="button" onMouseDown={(e) => { e.preventDefault(); submitSearch(e); }}
          data-testid="search-see-all"
          className="w-full text-left px-3 py-2 mt-1 border-t border-border text-sm font-semibold text-primary hover:bg-muted transition-colors">
          See all results for "{q.trim()}" →
        </button>
      </div>
    ) : null
  );

  return (
    <>
      <div className="steel-texture text-white text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto px-4 h-9 flex items-center justify-between">
          <span className="tracking-wide">Free tracked UK delivery · Royal Mail Tracked 48 · Genuine parts, fully warranted</span>
          <a href="tel:07443173458" className="hidden sm:flex items-center gap-1.5 hover:text-accent transition-colors" data-testid="phone-link">
            <Phone className="w-3.5 h-3.5" /> 07443 173458
          </a>
        </div>
      </div>

      <header className="sticky top-0 z-50 glass-nav border-b border-border">
        <div className="max-w-7xl mx-auto px-4">
          <div className="h-16 flex items-center gap-4">
            <button className="lg:hidden p-2 -ml-2" onClick={() => setMobile(!mobile)} data-testid="mobile-menu-toggle" aria-label="Menu">
              {mobile ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Logo />

            <div className="hidden md:block flex-1 max-w-3xl mx-4 relative">
              <form onSubmit={submitSearch} className="flex">
                <input
                  data-testid="search-input"
                  value={q} onChange={(e) => setQ(e.target.value)}
                  onFocus={() => suggestions.length && setSugOpen(true)}
                  onBlur={() => setTimeout(() => setSugOpen(false), 150)}
                  placeholder="Search products, part numbers or categories"
                  className="w-full h-11 px-4 bg-white border-2 border-secondary/40 rounded-l-sm text-sm shadow-sm focus:outline-none focus:border-primary transition-colors"
                />
                <button type="submit" data-testid="search-button"
                  className="h-11 px-6 bg-primary text-white font-semibold text-sm rounded-r-sm hover:bg-primary/90 transition-colors inline-flex items-center gap-2 border-2 border-primary">
                  <Search className="w-4 h-4" /> Search
                </button>
              </form>
              {renderSuggestions()}
            </div>

            <div className="ml-auto flex items-center gap-1">
              <Link to="/account" data-testid="account-link" className="p-2.5 rounded-sm hover:bg-muted transition-colors flex items-center gap-2">
                <User className="w-5 h-5" />
                <span className="hidden xl:inline text-sm font-medium">{user ? user.name.split(" ")[0] : "Account"}</span>
              </Link>
              <button data-testid="cart-button" onClick={() => setOpen(true)} className="relative p-2.5 rounded-sm hover:bg-muted transition-colors">
                <ShoppingCart className="w-5 h-5" />
                {count > 0 && (
                  <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-accent text-white text-[10px] font-bold min-w-[18px] h-[18px] grid place-items-center rounded-full">
                    {count}
                  </span>
                )}
              </button>
            </div>
          </div>

          <div className="md:hidden pb-3 relative" data-testid="mobile-search-bar">
            <form onSubmit={submitSearch} className="flex gap-2">
              <input
                data-testid="mobile-search-input"
                value={q} onChange={(e) => setQ(e.target.value)}
                onFocus={() => suggestions.length && setSugOpen(true)}
                onBlur={() => setTimeout(() => setSugOpen(false), 150)}
                placeholder="Search products, part numbers…"
                className="flex-1 h-11 px-4 bg-white border-2 border-secondary/40 rounded-sm text-sm shadow-sm focus:outline-none focus:border-primary transition-colors"
              />
              <button type="submit" data-testid="mobile-search-button" aria-label="Search"
                className="h-11 px-4 bg-primary text-white font-semibold text-sm rounded-sm shrink-0 hover:bg-primary/90 transition-colors">
                <Search className="w-4 h-4" />
              </button>
            </form>
            {renderSuggestions()}
          </div>

          <nav className="hidden lg:flex items-center gap-7 h-11 border-t border-border/60">
            {navLinks.map((c) => (
              <Link key={c.slug} to={`/collections/${c.slug}`} data-testid={`nav-${c.slug}`}
                className="text-sm font-medium text-secondary hover:text-primary transition-colors relative group">
                {c.name}
                <span className="absolute -bottom-[13px] left-0 w-0 h-0.5 bg-accent transition-all group-hover:w-full" />
              </Link>
            ))}
            <Link to="/collections/bleed-vents" data-testid="nav-bleed-vents" className="text-sm font-medium text-secondary hover:text-primary transition-colors">Accessories</Link>
            <Link to="/collections/bargain-bin" data-testid="nav-bargain-bin" className="text-sm font-bold text-accent hover:text-accent/80 transition-colors">Bargain Bin</Link>
            <Link to="/about" className="text-sm font-medium text-secondary hover:text-primary transition-colors ml-auto">About</Link>
            <div className="relative group">
              <Link to="/guides" data-testid="nav-guides" className="text-sm font-medium text-secondary hover:text-primary transition-colors inline-flex items-center gap-1">
                Guides <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
              </Link>
              <div className="absolute left-0 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all z-50" data-testid="guides-dropdown">
                <div className="bg-white border border-border rounded-sm shadow-hard py-2 w-72">
                  <span className="block px-4 pb-1.5 text-overline text-primary">Popular guides</span>
                  {POPULAR.map((gd) => (
                    <Link key={gd.slug} to={`/guides/${gd.slug}`} data-testid={`nav-guide-${gd.slug}`}
                      className="block px-4 py-2 text-sm text-secondary hover:bg-muted hover:text-primary transition-colors leading-snug">
                      {gd.title}
                    </Link>
                  ))}
                  <Link to="/guides" className="block px-4 py-2 mt-1 border-t border-border text-sm font-semibold text-primary hover:bg-muted transition-colors">
                    View all guides →
                  </Link>
                </div>
              </div>
            </div>
            <Link to="/contact" className="text-sm font-medium text-secondary hover:text-primary transition-colors">Contact</Link>
          </nav>
        </div>

        {mobile && (
          <div className="lg:hidden border-t border-border bg-white px-4 py-4 space-y-1">
            {navLinks.map((c) => (
              <Link key={c.slug} to={`/collections/${c.slug}`} className="block py-2.5 text-sm font-medium border-b border-border/50">{c.name}</Link>
            ))}
            <Link to="/collections/bleed-vents" className="block py-2.5 text-sm font-medium border-b border-border/50">Accessories</Link>
            <Link to="/collections/bargain-bin" className="block py-2.5 text-sm font-bold text-accent border-b border-border/50">Bargain Bin</Link>
            <Link to="/about" className="block py-2.5 text-sm font-medium border-b border-border/50">About</Link>
            <Link to="/guides" className="block py-2.5 text-sm font-medium border-b border-border/50">Guides</Link>
            <div className="pl-3 border-b border-border/50 pb-1">
              {POPULAR.map((gd) => (
                <Link key={gd.slug} to={`/guides/${gd.slug}`} className="block py-2 text-sm text-muted-foreground hover:text-primary">{gd.title}</Link>
              ))}
            </div>
            <Link to="/contact" className="block py-2.5 text-sm font-medium">Contact</Link>
          </div>
        )}
      </header>
      <CartDrawer />
    </>
  );
}
